import { useEffect, useRef, useState } from 'react';
import request from '../../../../../lib/request';

const AddExercise = ({
  dialogRef,
  closeDialog,
  selectedDetailType,
  handleSetSelectedExercises,
}) => {
  const bottomRef = useRef(null);
  const searchRef = useRef(null);
  const customExerciseRef = useRef(null);

  const [search, setSearch] = useState('');
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [selectedExerciseType, setSelectedExerciseType] = useState('');
  const [exercises, setExercises] = useState([]);
  const [customExercise, setCustomExercise] = useState('');

  useEffect(() => {
    const clickOutsideDialog = (e) => {
      if (
        dialogRef.current.contains(e.target) &&
        !bottomRef.current.contains(e.target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('click', clickOutsideDialog);

    return () => {
      document.removeEventListener('click', clickOutsideDialog);
    };
  }, []);

  useEffect(() => {
    if (selectedDetailType?.length > 0) {
      getExerciseList();
    }
  }, [selectedDetailType]);

  const getExerciseList = async (query = '') => {
    try {
      let url = '/static/exercise';
      if (query) {
        url += `?query=${query}`;
      }
      const { result } = await request.get(url);
      if (result) {
        setExercises(result.map((item) => item.name));
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(searchRef.current);
    searchRef.current = setTimeout(async () => {
      await getExerciseList(value);
    }, 500);
  };

  const handleAddExercise = (exercise) => {
    const exerciseLog = {
      name: exercise,
      sets: [{ weight: 0, count: 0, unit: 'reps' }],
    };
    setSelectedExercises([...selectedExercises, exerciseLog]);
  };

  const handleAddCustomExercise = () => {
    if (customExercise.trim() !== '') {
      const exerciseLog = {
        name: customExercise.trim(),
        sets: [{ weight: 0, count: 0, unit: 'reps' }],
      };
      setSelectedExercises([...selectedExercises, exerciseLog]);
    }
    setCustomExercise('');
    closeCustomExerciseDialog();
  };

  const handleRemoveExercise = (index) => {
    setSelectedExercises(selectedExercises.filter((e, i) => i !== index));
  };

  const handelDone = () => {
    if (selectedExercises.length > 0) {
      if (selectedExerciseType !== '') {
        const deepCopy = selectedExercises.map((exercise) => ({
          ...exercise,
          sets: [...exercise.sets],
        }));
        const groupedExercise = deepCopy.reduce((acc, cur) => {
          if (Array.isArray(acc.name)) {
            acc.name.push(cur.name);
          } else {
            acc.name = [acc.name, cur.name];
          }
          if (Array.isArray(acc.sets[0])) {
            acc.sets[0].push(cur.sets[0]);
          } else {
            acc.sets = [[acc.sets[0], cur.sets[0]]];
          }
          return acc;
        });
        groupedExercise.type = selectedExerciseType;
        groupedExercise.groupId = Math.random().toString(36).slice(2);
        handleSetSelectedExercises([groupedExercise]);
      } else {
        handleSetSelectedExercises(selectedExercises);
      }
    }
    handleClose();
  };

  const handleClose = () => {
    setSearch('');
    setSelectedExercises([]);
    setSelectedExerciseType('');
    closeDialog();
  };

  const handleSelectExerciseType = (type) => {
    if (type === selectedExerciseType) {
      setSelectedExerciseType('');
    } else {
      setSelectedExerciseType(type);
    }
  };

  const openCustomExerciseDialog = () => {
    customExerciseRef.current.showModal();
  };

  const closeCustomExerciseDialog = () => {
    customExerciseRef.current.close();
  };

  return (
    <>
      <dialog
        ref={dialogRef}
        className='w-full h-full max-w-lg max-h-full bg-transparent'
      >
        <div
          ref={bottomRef}
          className={`bg-secondary fixed bottom-0 w-full max-w-lg rounded-t-3xl px-3 py-6 h-[90%] flex flex-col`}
        >
          <div className='flex items-center justify-between mb-4 text-white'>
            <h1 className='text-xl font-bold flex flex-wrap gap-1'>
              {selectedDetailType.map((item, i) => (
                <div key={i}>
                  <span className='text-accent'>{item}</span>{' '}
                  {i < selectedDetailType.length - 1 && '|'}
                </div>
              ))}{' '}
              Exercises
            </h1>
            <button
              onClick={handleClose}
              className='text-2xl'
            >
              🅧
            </button>
          </div>
          <div className='flex flex-col gap-4 flex-shrink-0'>
            <div className='w-full relative'>
              <input
                type='text'
                placeholder='Search exercise by name'
                className='rounded-xl pl-8 pr-2 py-2 bg-tertiary  text-white w-full'
                value={search}
                onChange={handleSearch}
              />
              <span className='absolute left-2 top-1/2 -translate-y-1/2'>
                🔍
              </span>
            </div>
            <div className='space-y-2'>
              <div className='flex items-center justify-between'>
                <div className='font-semibold text-xs text-white/50'>
                  Selected {selectedExercises.length}
                </div>
                <div className='flex items-center gap-2'>
                  <span
                    onClick={() => handleSelectExerciseType('superset')}
                    className={`text-xs rounded-full px-2 py-1 border ${
                      selectedExerciseType === 'superset'
                        ? 'bg-protein/40 text-white font-bold border-protein'
                        : 'border-dotted text-white/50 border-tertiary'
                    }`}
                  >
                    superset
                  </span>
                  <span
                    onClick={() => handleSelectExerciseType('compound set')}
                    className={`text-xs rounded-full px-2 py-1 border ${
                      selectedExerciseType === 'compound set'
                        ? 'bg-carbs/40 text-white font-bold border-carbs'
                        : 'border-dotted text-white/50 border-tertiary'
                    }`}
                  >
                    compound set
                  </span>
                </div>
              </div>
              {selectedExercises.length > 0 && (
                <div
                  className={`flex items-center gap-3 overflow-x-auto py-3 scrollbar-hidden ${
                    selectedExerciseType === 'superset'
                      ? 'border border-dashed border-protein rounded-xl px-3 bg-protein/10'
                      : selectedExerciseType === 'compound set'
                      ? 'border border-dashed border-carbs rounded-xl px-3 bg-carbs/10'
                      : ''
                  }`}
                >
                  {selectedExercises.map((exercise, i) => (
                    <div
                      key={i}
                      className='shrink-0 flex items-center gap-3 text-white bg-tertiary/50 rounded-lg px-4 py-2 border border-dashed border-tertiary'
                    >
                      <div>{exercise.name}</div>
                      <button onClick={() => handleRemoveExercise(i)}>x</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className='text-white/50 text-xs flex justify-end mt-4'>
            {exercises.length} exercises found
          </div>

          {exercises.length > 0 ? (
            <div className='flex-1 overflow-y-auto divide-y-[1px] divide-tertiary mt-1 h-full'>
              {exercises.map((exercise, i) => (
                <button
                  key={i}
                  className='text-white p-4 hover:bg-tertiary/50 block w-full text-left'
                  onClick={() => handleAddExercise(exercise)}
                >
                  {exercise}
                </button>
              ))}
            </div>
          ) : (
            <div className='flex justify-center items-center h-full'>
              <div className='text-center'>
                <div className='text-tertiary mb-2'>No exercises found</div>
                <button
                  className='border border-dashed border-tertiary rounded-full px-4 py-2 text-white/50'
                  onClick={openCustomExerciseDialog}
                >
                  + Custom exercise
                </button>
              </div>
            </div>
          )}

          <div className='flex-shrink-0 pt-4'>
            <button
              className='bg-primary text-white py-3 rounded-full w-full'
              onClick={handelDone}
            >
              Done
            </button>
          </div>
        </div>
      </dialog>
      <dialog
        ref={customExerciseRef}
        className='bg-tertiary rounded-xl p-4'
      >
        <div className='flex items-center justify-between text-white mb-4'>
          <h1 className='font-bold '>Custom exercise</h1>
          <button
            onClick={closeCustomExerciseDialog}
            className=''
          >
            🅧
          </button>
        </div>
        <div className='space-y-3'>
          <input
            type='text'
            placeholder='Exercise name'
            className='rounded-full px-2 py-2  w-full'
            value={customExercise}
            autoFocus={true}
            onChange={(e) => setCustomExercise(e.target.value)}
          />
          <button
            className='bg-primary text-white py-2 rounded-full w-full '
            onClick={handleAddCustomExercise}
          >
            Add
          </button>
        </div>
      </dialog>
    </>
  );
};

export default AddExercise;
